'use client';

import React from 'react';

import { Box, BoxProps } from './box';
import { HStack, VStack } from './stack';
import { Tabs } from './tabs';
import { Text } from './text';

export interface IconTabItem {
  value: string;
  icon: React.ReactNode;
  label?: string;
  content: React.ReactNode;
  disabled?: boolean;
}

export interface IconTabsProps extends BoxProps {
  items: IconTabItem[];
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  variant?: 'line' | 'enclosed' | 'soft-rounded';
  orientation?: 'horizontal' | 'vertical';
  size?: 'sm' | 'md' | 'lg';
  showLabels?: boolean;
  iconPosition?: 'top' | 'left' | 'right' | 'bottom';
}

/**
 * IconTabs component that provides icon-based tab navigation
 * Built on top of the compound Tabs system with icon-focused design
 */
export const IconTabs: React.FC<IconTabsProps> = ({
  items,
  defaultValue,
  value,
  onValueChange,
  variant = 'line',
  orientation = 'horizontal',
  size = 'md',
  showLabels = true,
  iconPosition = 'top',
  ...props
}) => {
  const sizeConfig = {
    sm: {
      padding: 2,
      iconSize: 16,
      fontSize: 'xs',
      gap: 1,
    },
    md: {
      padding: 3,
      iconSize: 20,
      fontSize: 'sm',
      gap: 2,
    },
    lg: {
      padding: 4,
      iconSize: 24,
      fontSize: 'md',
      gap: 3,
    },
  };

  const config = sizeConfig[size];

  const getTabContent = (item: IconTabItem) => {
    if (!showLabels) {
      return (
        <Box
          width={`${config.iconSize}px`}
          height={`${config.iconSize}px`}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {item.icon}
        </Box>
      );
    }

    const iconElement = (
      <Box
        width={`${config.iconSize}px`}
        height={`${config.iconSize}px`}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        {item.icon}
      </Box>
    );

    const labelElement = item.label && (
      <Text fontSize={config.fontSize} lineHeight="1.2">
        {item.label}
      </Text>
    );

    switch (iconPosition) {
      case 'left':
        return (
          <HStack gap={config.gap} align="center">
            {iconElement}
            {labelElement}
          </HStack>
        );
      case 'right':
        return (
          <HStack gap={config.gap} align="center">
            {labelElement}
            {iconElement}
          </HStack>
        );
      case 'bottom':
        return (
          <VStack gap={config.gap} align="center">
            {labelElement}
            {iconElement}
          </VStack>
        );
      case 'top':
      default:
        return (
          <VStack gap={config.gap} align="center">
            {iconElement}
            {labelElement}
          </VStack>
        );
    }
  };

  return (
    <Box {...props}>
      <Tabs.Root
        defaultValue={defaultValue || items[0]?.value}
        value={value}
        onValueChange={onValueChange}
        variant={variant}
        orientation={orientation}
      >
        <VStack align="stretch" gap={4}>
          {/* Tab List */}
          <Box>
            <Tabs.List>
              {items.map((item) => (
                <Tabs.Trigger
                  key={item.value}
                  value={item.value}
                  disabled={item.disabled}
                  padding={config.padding}
                  minWidth={showLabels ? 'auto' : `${config.iconSize + config.padding * 2}px`}
                  height={
                    (showLabels && iconPosition === 'top') || iconPosition === 'bottom'
                      ? 'auto'
                      : `${config.iconSize + config.padding * 2}px`
                  }
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  title={item.label} // Tooltip for icon-only mode
                >
                  {getTabContent(item)}
                </Tabs.Trigger>
              ))}
              {variant === 'line' && <Tabs.Indicator />}
            </Tabs.List>
          </Box>

          {/* Tab Contents */}
          <Box>
            <Tabs.ContentGroup>
              {items.map((item) => (
                <Tabs.Content key={item.value} value={item.value}>
                  {item.content}
                </Tabs.Content>
              ))}
            </Tabs.ContentGroup>
          </Box>
        </VStack>
      </Tabs.Root>
    </Box>
  );
};

export default IconTabs;
